// Berat pengiriman (gram) — helper murni.
//
// Admin mencatat BERAT total kiriman saat "Buat Nota + Kirim" atau "Set
// Ongkir". Berat DIHITUNG OTOMATIS dari berat produk (berat satuan varian ×
// qty). Bila ADA item yang berat produknya belum diisi, total dikosongkan
// (null) → UI mengosongkan input agar admin mengisi berat total manual.
//
// `computeAutoShipmentWeight` = cermin TS dari SQL `shipment_weight_from_items`
// (mig 221) — sumber rumus tunggal, pola dp.ts ⇄ compute_order_dp_total.
//
// BERAT TARIF (mig 7280) ≠ berat paket: untuk MENGHITUNG/MENGUTIP ongkir,
// varian tanpa berat dihitung `DEFAULT_ITEM_WEIGHT_GRAM` per pcs
// (`computeRateWeight` ⇄ SQL `shipment_rate_weight_from_items`). Berat PAKET
// yang dicatat admin tetap memakai aturan di atas — kosong bila ada produk
// tanpa berat.

export type WeightSourceItem = {
  qty: number;
  /** Berat satuan produk/varian (gram). null/≤0 = belum diisi di produk. */
  weightGram: number | null | undefined;
};

/**
 * Total berat (gram) = Σ (berat satuan × qty). Mengembalikan null bila:
 *  • tidak ada item ber-qty > 0, ATAU
 *  • ADA item ber-qty yang beratnya kosong (null/≤0) — pemanggil mengosongkan
 *    input agar admin mengisi berat total manual.
 * Item ber-qty ≤ 0 diabaikan (mis. baris non-fisik).
 */
export function computeAutoShipmentWeight(
  items: ReadonlyArray<WeightSourceItem>
): number | null {
  let total = 0;
  let counted = 0;
  for (const it of items) {
    const qty = Math.max(0, Math.trunc(Number(it.qty) || 0));
    if (qty <= 0) continue;
    const w = it.weightGram;
    if (w == null || !(Number(w) > 0)) return null;
    total += Math.round(Number(w)) * qty;
    counted += 1;
  }
  return counted > 0 ? total : null;
}

/**
 * Berat satuan (gram) untuk varian TANPA berat saat menghitung ongkir
 * (putusan pemilik 2026-10-14: semua toko, saklar Multigudang ON & OFF, per
 * pcs). Cermin SQL `default_item_weight_gram()` (mig 7280) — geser keduanya
 * bersama (dijaga `defaultItemWeightLint.test.ts`).
 */
export const DEFAULT_ITEM_WEIGHT_GRAM = 500;

export type RateWeight = {
  /**
   * Berat tarif (gram). `null` = tak ada barang ber-qty, ATAU ada barang yang
   * beratnya TAK TERBACA (lihat `computeRateWeight`) — jangan mengutip.
   */
  gram: number | null;
  /** Pcs yang dihitung dengan `DEFAULT_ITEM_WEIGHT_GRAM` (varian tanpa berat). */
  defaultedPcs: number;
};

/**
 * BERAT TARIF = Σ (berat varian > 0 ? berat : `DEFAULT_ITEM_WEIGHT_GRAM`) ×
 * qty — cermin SQL `shipment_rate_weight_from_items` (mig 7280), yang dipakai
 * gerbang `consume_shipping_quote` untuk memeriksa ulang kutipan. HANYA untuk
 * mengutip ongkir; berat paket tetap `computeAutoShipmentWeight`.
 *
 * Tiga keadaan `weightGram` per barang, dan pembedaannya soal uang:
 *  • angka > 0        → berat varian;
 *  • `null` / ≤ 0     → varian tanpa berat → berat default per pcs;
 *  • `undefined`      → beratnya TAK TERBACA (gagal memuat / baris tak
 *    terlihat) → total `null`. Kegagalan jaringan tak boleh diam-diam
 *    menjadi 500 g/pcs — itu angka yang ditagihkan.
 * Barang ber-qty ≤ 0 diabaikan.
 */
export function computeRateWeight(items: ReadonlyArray<WeightSourceItem>): RateWeight {
  let total = 0;
  let counted = 0;
  let defaultedPcs = 0;
  for (const it of items) {
    const qty = Math.max(0, Math.trunc(Number(it.qty) || 0));
    if (qty <= 0) continue;
    const w = it.weightGram;
    if (w === undefined) return { gram: null, defaultedPcs: 0 };
    if (w === null || !(Number(w) > 0)) {
      total += DEFAULT_ITEM_WEIGHT_GRAM * qty;
      defaultedPcs += qty;
    } else {
      total += Math.round(Number(w)) * qty;
    }
    counted += 1;
  }
  return { gram: counted > 0 ? total : null, defaultedPcs };
}

/**
 * Kalimat WAJIB setiap layar yang mengutip dengan berat default (lihat "Berat
 * CADANGAN" di bawah: angka default hanya boleh dipakai bila TERTULIS) — satu
 * kalimat untuk kartu tarif, sheet sinkron ongkir admin, dan keranjang katalog.
 * `defaultedPcs` ≤ 0 → "" (tak ada yang perlu ditulis).
 */
export function defaultWeightNoteText(
  defaultedPcs: number,
  totalGram?: number | null
): string {
  const pcs = Math.max(0, Math.trunc(Number(defaultedPcs) || 0));
  if (pcs <= 0) return "";
  const total =
    totalGram != null && Number(totalGram) > 0
      ? ` (total ${formatWeightGram(totalGram)})`
      : "";
  return `${pcs} pcs belum ada beratnya — ongkir dihitung dengan berat default ${formatWeightGram(
    DEFAULT_ITEM_WEIGHT_GRAM
  )}/pcs${total}.`;
}

/**
 * Parse input berat (gram) dari form → integer ≥ 0, atau null bila kosong /
 * tak valid (buang non-digit; input manual admin selalu bulat & non-negatif).
 */
export function parseWeightInput(raw: string): number | null {
  const cleaned = (raw ?? "").replace(/[^\d]/g, "");
  if (cleaned === "") return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? Math.max(0, Math.trunc(n)) : null;
}

/** Bentuk mentah (isi input) sebuah berat otomatis. null/undefined → kosong. */
export function weightRawOf(gram: number | null | undefined): string {
  return gram == null ? "" : String(gram);
}

/**
 * Isi kolom "Berat Kiriman" berikutnya saat berat OTOMATIS berubah — dipakai
 * form "Buat Nota + Kirim" yang isi notanya masih bisa berubah sambil form
 * terbuka (admin menambah/mengurangi item terpilih).
 *
 * Aturannya SATU kalimat: kolom itu milik hitungan otomatis SAMPAI admin
 * mengetiknya sendiri. Kepemilikan dinilai dari isi kolom vs berat otomatis
 * SEBELUMNYA (`prevAutoGram`) — bukan dari flag "pernah difokus": admin bisa
 * mengklik kolomnya lalu tak mengubah apa pun, dan itu bukan alasan untuk
 * berhenti menyegarkan angkanya.
 *
 *  • `autoGram === undefined` → pemanggil tidak menyediakan berat item sama
 *    sekali (server yang menghitung) → JANGAN sentuh isi kolom.
 *  • admin sudah mengetik nilai lain → biarkan; angka yang ia tulis adalah
 *    hasil timbangan, dan menimpanya diam-diam mengubah dasar ongkir.
 *  • selain itu → ikut berat otomatis; termasuk saat ia jadi null (ada produk
 *    tanpa berat) → kolom DIKOSONGKAN. Angka lama di situ milik kita, dan
 *    membiarkannya berarti mengklaim berat untuk isi paket yang sudah berbeda.
 */
export function syncAutoWeightRaw(args: {
  currentRaw: string;
  autoGram: number | null | undefined;
  prevAutoGram: number | null | undefined;
}): string {
  const { currentRaw, autoGram, prevAutoGram } = args;
  if (autoGram === undefined) return currentRaw;
  const ownedByAuto =
    currentRaw.trim() === "" || currentRaw === weightRawOf(prevAutoGram);
  return ownedByAuto ? weightRawOf(autoGram) : currentRaw;
}

/** Asal angka berat yang ditampilkan — menentukan seberapa jauh ia dipercaya. */
export type ShipmentWeightSource =
  /** `shipment_requests.weight_gram` — diisi admin, dasar ongkir yang ditagih. */
  | "stored"
  /** Dihitung dari berat produk × qty — PERKIRAAN, bukan yang ditimbang. */
  | "auto"
  /** Tak tersimpan DAN tak bisa dihitung (ada produk yang beratnya kosong). */
  | "unknown";

export type ResolvedShipmentWeight = {
  gram: number | null;
  source: ShipmentWeightSource;
};

/**
 * SATU aturan "berat mana yang ditampilkan", dipakai bersama halaman
 * pengiriman (admin & pembeli) dan kartu Status Pengiriman di kedua halaman
 * tagihan. Kalau tiap layar memutuskan sendiri, satu paket bisa terbaca
 * "1,2 kg" di satu halaman dan "belum diisi" di halaman lain.
 *
 * Urutan: berat TERSIMPAN menang (itulah yang dipakai admin menghitung ongkir
 * yang ditagihkan) → hitung otomatis dari berat produk → tidak diketahui.
 *
 * `stored` 0 gram DIHORMATI sebagai nilai yang sengaja diisi: kolomnya
 * nullable, jadi "belum diisi" sudah punya bentuknya sendiri (NULL) dan 0
 * hanya bisa datang dari admin yang mengetiknya.
 */
export function resolveShipmentWeight(
  stored: number | null | undefined,
  items: ReadonlyArray<WeightSourceItem> = []
): ResolvedShipmentWeight {
  const n = Number(stored);
  if (stored != null && Number.isFinite(n) && n >= 0) {
    return { gram: Math.round(n), source: "stored" };
  }
  const auto = computeAutoShipmentWeight(items);
  if (auto != null) return { gram: auto, source: "auto" };
  return { gram: null, source: "unknown" };
}

/**
 * Berat isi paket SEKARANG bila berat TERSIMPAN tertinggal di bawahnya — tanda
 * berat basi: barang ditambahkan (Revisi Isi Paket) atau berat produk dinaikkan
 * sesudah beratnya dicatat. `null` = tak ada yang perlu diperingatkan.
 *
 * Hanya arah "tersimpan LEBIH RINGAN" yang dianggap basi. Timbangan sungguhan
 * ikut menimbang kardus & plastik, jadi berat ketikan admin hampir selalu ≥ Σ
 * berat produknya (audit PROD 2026-10-22: 72 dari 82 berat ketikan begitu),
 * sedangkan berat yang lebih ringan dari barangnya sendiri mustahil hasil
 * timbang. Arah sebaliknya dibiarkan — itu bentuk wajar berat timbangan.
 *
 * Tak diketahui (tersimpan kosong, atau ada produk tanpa berat) → `null`:
 * tanpa dua angka tak ada yang bisa dibandingkan. 0 gram juga `null` — itu
 * nilai yang sengaja diketik admin (lihat `resolveShipmentWeight`).
 */
export function storedWeightBehindContents(
  stored: number | null | undefined,
  items: ReadonlyArray<WeightSourceItem> = []
): number | null {
  if (stored == null) return null;
  const s = Number(stored);
  if (!Number.isFinite(s) || s <= 0) return null;
  const contents = computeAutoShipmentWeight(items);
  if (contents == null) return null;
  return Math.round(s) < contents ? contents : null;
}

/**
 * Teks siap tampil. Hasil hitung otomatis diberi awalan "±" — ia PERKIRAAN
 * dari berat produk, bukan berat yang ditimbang kurir, dan menyamakan
 * tampilannya dengan berat tersimpan membuat pembaca mengira ongkirnya sudah
 * dihitung dari angka itu.
 */
export function shipmentWeightText(w: ResolvedShipmentWeight): string {
  if (w.gram == null) return "belum diisi";
  const label = formatWeightGram(w.gram);
  return w.source === "auto" ? `± ${label}` : label;
}

/**
 * Tampilan ringkas berat: "900 g" atau "1,2 kg" (koma Indonesia, buang ,0).
 * null / negatif → "—".
 */
export function formatWeightGram(gram: number | null | undefined): string {
  if (gram == null || !(Number(gram) >= 0)) return "—";
  const g = Math.round(Number(gram));
  if (g < 1000) return `${g} g`;
  const kg = g / 1000;
  const s = (Math.round(kg * 10) / 10).toFixed(1).replace(/\.0$/, "").replace(".", ",");
  return `${s} kg`;
}

// ─── Berat CADANGAN untuk daftar tarif (permintaan pemilik) ────────────────
//
// Sampai di sini seluruh modul memegang satu aturan: berat yang tak bisa
// dihitung adalah `null`, dan `null` berarti TIDAK ADA ANGKA — layar yang
// memanggil route tarif dengan berat kosong akan menerima kutipan 1 kg
// (`weightBucketGram` berlantai `MIN_WEIGHT_BUCKET_GRAM`) tanpa satu pun tanda
// bahwa pelantaian itu terjadi.
//
// Pengecualiannya (pemilik 2026-10-14, menggantikan cadangan 1 kg per kiriman
// yang dulu hanya untuk layar pemesanan): SEMUA kartu tarif, pembeli maupun
// admin, mengutip dengan BERAT TARIF (`computeRateWeight` — 500 g per pcs
// tanpa berat) bila berat layar kosong, dan gerbang uang di SQL memeriksa
// dengan aturan yang sama sehingga kutipannya benar-benar tertagih.
// Kewajibannya tetap: berat default hanya boleh dipakai bila layarnya
// MENULISKAN bahwa angkanya memuat berat default — itulah gunanya `isDefault`
// dipulangkan berdampingan dengan `gram`. Kolom Berat Kiriman admin sendiri
// tidak diisi angka default; ia tetap berat PAKET.

export type EstimateWeight = {
  /**
   * Gram yang dikirim ke route tarif.
   *  • `number`    — boleh mengutip;
   *  • `null`      — tak diketahui DAN tak ada cadangan → jangan mengutip;
   *  • `undefined` — berat masih dimuat → jangan mengutip DULU (bedanya dengan
   *    `null`: layar tak boleh menuduh produk tanpa berat sebelum tahu).
   */
  gram: number | null | undefined;
  /** `true` bila `gram` adalah berat DEFAULT, bukan berat produk. */
  isDefault: boolean;
};

/**
 * Berat yang dipakai daftar tarif, berikut kejujuran tentang asalnya.
 *
 * `autoGram` = berat yang sudah dipegang layar — hasil
 * `computeAutoShipmentWeight` (lewat `useShipmentItemsWeight`) di sisi pembeli,
 * ATAU angka yang diketik admin (`parseWeightInput`) di layar ber-kolom berat;
 * `fallbackGram` = berat cadangan opsional. Sejak mig 7280 `RateOptions` tak
 * lagi memakainya (berat kosong → berat tarif per pcs dari isi kiriman,
 * `computeRateWeight`); parameternya tinggal untuk pemanggil yang memang punya
 * satu angka cadangan. Cadangan diabaikan bila tidak positif — berat 0 atau
 * negatif bukan cadangan, ia bug pemanggil.
 *
 * ⚠️ **0 gram DIHORMATI sebagai berat yang sengaja diisi**, tidak jatuh ke
 * cadangan — aturan yang sama persis dengan `resolveShipmentWeight` di atas,
 * dan alasannya sama: "belum diisi" sudah punya bentuknya sendiri (null), jadi
 * 0 hanya bisa datang dari admin yang mengetiknya. Menyamakan keduanya adalah
 * REGRESI yang sempat tayang (#1043): admin yang mengetik "0" di kolom Berat
 * Kiriman kehilangan seluruh daftar tarif yang sebelumnya ia lihat, di empat
 * layar sekaligus. Berat NEGATIF tetap ditolak — itu rusak, bukan disengaja.
 *
 * Sisi pembeli tak terpengaruh aturan ini: `computeAutoShipmentWeight` tak
 * pernah memulangkan 0 (ia memulangkan null begitu ada satu item tanpa berat),
 * jadi 0 hanya bisa lahir dari kolom berat admin.
 *
 * Berat masih dimuat (`undefined`) TIDAK jatuh ke cadangan: mengutip berat
 * cadangan lebih dulu lalu menggantinya dengan berat sungguhan sedetik kemudian
 * berarti pembeli sempat melihat harga yang bukan harganya.
 */
export function resolveEstimateWeight(
  autoGram: number | null | undefined,
  fallbackGram?: number | null
): EstimateWeight {
  if (autoGram === undefined) return { gram: undefined, isDefault: false };
  const auto = Number(autoGram);
  if (autoGram !== null && Number.isFinite(auto) && auto >= 0) {
    return { gram: auto, isDefault: false };
  }
  const fb = Number(fallbackGram);
  if (fallbackGram != null && Number.isFinite(fb) && fb > 0) {
    return { gram: Math.round(fb), isDefault: true };
  }
  return { gram: null, isDefault: false };
}
